import React from 'react'
import Button, { LinkButton } from '@adsk/alloy-react-button'
import {
	type ModalContextProps,
	useModalContext,
	FileHandlerUtils,
} from '@adsk/napi-shared'
import { Flex } from '@adsk/alloy-react-box'
import { type FileUploadResponse } from '@adsk/napi-api-client'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../../store'
import {
	type FileInfoType,
	type OnboardingUserAppErrorType,
} from '../../../types/ActionTypes'
import { PROFILE_EDIT_CONFIRMATION_STEP_IDX } from '../../profile-edit-confirmation'
import Theme from '@adsk/alloy-react-theme'
import {
	resetCurrentAvatarSelectionState,
	resetState,
	setErrorInfo,
	setSubmitState,
	setUserInfo,
	showLoadingState,
	setProfilePictureAction,
} from '../../../store/actions'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../../types/AnalyticsTypes'
import { getPackageInfo } from '../../../utils'
import { BugsnagNotify } from '@adsk/napi-bugsnag'
import { useTranslation } from '../../../../i18n'

const FooterComponent = (): JSX.Element => {
	const { name, version } = getPackageInfo()
	const { t } = useTranslation()
	const { spacing } = Theme
	const {
		state: { fileInfo, submitState, showLoader },
		dispatch,
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const { jumpTo } = useModalContext() as ModalContextProps

	const fileHandlers = async () => {
		const { action } = (fileInfo || {}) as FileInfoType
		switch (action) {
			case 'remove':
				dispatch(
					setUserInfo({
						updatedAvatarUrl: '',
					})
				)
				jumpTo(PROFILE_EDIT_CONFIRMATION_STEP_IDX)
				return
			case 'upload':
				await uploadFile()
				break
		}
	}
	const uploadFile = async () => {
		const {
			file,
			cropProps: { pixelCrop, canvasRef },
			imageRefElement,
		} = (fileInfo || {}) as FileInfoType
		if (file) {
			try {
				dispatch(showLoadingState(true))
				const fileObject = await FileHandlerUtils.convertImageToFile(
					imageRefElement as HTMLImageElement,
					canvasRef as HTMLCanvasElement,
					pixelCrop,
					file.name,
					file.type
				)
				const fileUploadResponse: FileUploadResponse =
					await FileHandlerUtils.uploadFile({
						objectName: file.name,
						contentType: file.type,
						binary: fileObject,
					})
				dispatch(
					setUserInfo({
						updatedAvatarUrl: fileUploadResponse?.signedUrl,
					})
				)
				jumpTo(PROFILE_EDIT_CONFIRMATION_STEP_IDX)
			} catch (e) {
				// send error to bugsnag
				BugsnagNotify(e as Error, {
					message: 'Builders Network Profile Picture API Error',
				})
				track(ANALYTICS_TYPES.NAPI_USER_PROFILE_PICTURE_UPLOADING_FAILED, {
					package: name,
					version,
				})
				dispatch(
					setErrorInfo({
						message: t('SERVER_ERROR'),
					} as OnboardingUserAppErrorType)
				)
			} finally {
				dispatch(setProfilePictureAction(undefined))
				dispatch(showLoadingState(false))
			}
		} else {
			dispatch(setProfilePictureAction(undefined))
			dispatch(
				setErrorInfo({
					message: t('FILE_REQUIRED'),
				} as OnboardingUserAppErrorType)
			)
		}
	}

	return (
		<Flex justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
			<Flex flexDirection={'row'}>
				<LinkButton
					onClick={() => {
						dispatch(
							setErrorInfo({
								message: undefined,
							})
						)
						dispatch(resetCurrentAvatarSelectionState())
						dispatch(setSubmitState(false))
					}}
					style={{
						...(fileInfo?.action !== 'upload' || showLoader
							? { display: 'none' }
							: {}),
					}}
					disabled={fileInfo?.action !== 'upload' || showLoader}
					size={LinkButton.SIZES.SMALL}
				>
					{t('REMOVE_IMAGE')}
				</LinkButton>
			</Flex>
			<Flex flexDirection={'row'}>
				<Flex marginX={spacing.small}>
					<LinkButton
						onClick={() => {
							dispatch(resetState())
							dispatch(resetCurrentAvatarSelectionState())
							jumpTo(PROFILE_EDIT_CONFIRMATION_STEP_IDX)
						}}
						size={LinkButton.SIZES.SMALL}
						disabled={Boolean(showLoader) ?? false}
					>
						{t('CANCEL')}
					</LinkButton>
				</Flex>
				<Flex marginX={spacing.small}>
					<Button
						variant={Button.VARIANTS.PRIMARY}
						onClick={fileHandlers}
						disabled={!submitState || showLoader}
					>
						{t('SAVE')}
					</Button>
				</Flex>
			</Flex>
		</Flex>
	)
}

export default FooterComponent
